import type { WSClientEvent, WSServerEvent } from '@goodchat/shared/types';

type EventHandler = (event: WSServerEvent) => void;

export class WebSocketClient {
  private ws: WebSocket | null = null;
  private handlers = new Set<EventHandler>();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  private maxReconnectDelay = 30_000;
  private maxReconnectAttempts = 50;
  private getToken: (() => string | null) | null = null;
  private _isConnected = false;
  private _wasConnectedBefore = false;
  /**
   * Refcounted set of area subscriptions the app WANTS active. Server-side
   * subscriptions are per-connection, so this is replayed on every socket
   * open — without it, realtime silently dies after any reconnect.
   */
  private desired = new Map<string, number>();

  get isConnected(): boolean {
    return this._isConnected;
  }

  connect(getToken: (() => string | null) | string): void {
    // Support both callback (preferred) and static token (backward compat)
    if (typeof getToken === 'function') {
      this.getToken = getToken;
    } else {
      const staticToken = getToken;
      this.getToken = () => staticToken;
    }
    // Idempotent: if a socket is already open/connecting, keep it (the token
    // getter has been swapped; the next reconnect will use it).
    if (
      this.ws &&
      (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }
    this.reconnectAttempts = 0;
    this._wasConnectedBefore = false;
    this.doConnect();
  }

  /**
   * Declare interest in areas. Refcounted: multiple subscribers to the same
   * area send a single server subscribe; the subscription is dropped only
   * when the last subscriber unsubscribes. Safe to call while disconnected —
   * the desired set is replayed on every open.
   */
  subscribeAreas(areaIds: string[]): void {
    const newlyAdded: string[] = [];
    for (const id of areaIds) {
      const count = this.desired.get(id) ?? 0;
      this.desired.set(id, count + 1);
      if (count === 0) newlyAdded.push(id);
    }
    if (newlyAdded.length > 0) {
      this.send({ event: 'subscribe', areaIds: newlyAdded } as WSClientEvent);
    }
  }

  unsubscribeAreas(areaIds: string[]): void {
    const dropped: string[] = [];
    for (const id of areaIds) {
      const count = this.desired.get(id) ?? 0;
      if (count <= 1) {
        this.desired.delete(id);
        if (count === 1) dropped.push(id);
      } else {
        this.desired.set(id, count - 1);
      }
    }
    if (dropped.length > 0) {
      this.send({ event: 'unsubscribe', areaIds: dropped } as WSClientEvent);
    }
  }

  private doConnect(): void {
    const token = this.getToken?.();
    if (!token) return;

    // Tear down any previous socket so it can't leak or double-deliver events.
    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      try {
        this.ws.close();
      } catch {
        // already closed
      }
      this.ws = null;
    }

    try {
      let wsUrl: string;
      const apiUrl = import.meta.env.VITE_API_URL;
      if (apiUrl) {
        const parsed = new URL(apiUrl);
        const wsProtocol = parsed.protocol === 'https:' ? 'wss:' : 'ws:';
        wsUrl = `${wsProtocol}//${parsed.host}/ws?token=${token}`;
      } else {
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        wsUrl = `${protocol}//${window.location.host}/ws?token=${token}`;
      }
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        const wasReconnect = this._wasConnectedBefore;
        this._isConnected = true;
        this._wasConnectedBefore = true;
        this.reconnectAttempts = 0;
        // Replay subscriptions BEFORE notifying, so `connected`/`reconnected`
        // handlers (which refetch missed data) run with live subscriptions —
        // the refetch covers the gap, the replay covers the future.
        if (this.desired.size > 0) {
          this.send({ event: 'subscribe', areaIds: [...this.desired.keys()] } as WSClientEvent);
        }
        this.notify({ event: 'connected' } as unknown as WSServerEvent);
        if (wasReconnect) {
          this.notify({ event: 'reconnected' } as unknown as WSServerEvent);
        }
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data) as WSServerEvent;
          this.notify(data);
        } catch {
          // Ignore invalid messages
        }
      };

      this.ws.onclose = () => {
        this._isConnected = false;
        this.notify({ event: 'disconnected' } as unknown as WSServerEvent);
        this.scheduleReconnect();
      };

      this.ws.onerror = () => {
        // Error followed by close event
      };
    } catch {
      this.scheduleReconnect();
    }
  }

  disconnect(): void {
    this.getToken = null;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.close();
      this.ws = null;
    }
    this._isConnected = false;
    // Desired subscriptions persist — React effect cleanups balance the
    // refcounts; a later connect() replays whatever is still wanted.
  }

  send(event: WSClientEvent): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(event));
    }
  }

  subscribe(handler: EventHandler): () => void {
    this.handlers.add(handler);
    return () => {
      this.handlers.delete(handler);
    };
  }

  private notify(event: WSServerEvent): void {
    for (const handler of this.handlers) {
      try {
        handler(event);
      } catch (err) {
        console.error('WS handler error:', err);
      }
    }
  }

  private scheduleReconnect(): void {
    if (!this.getToken) return;
    if (this.reconnectTimer) return;

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this.notify({ event: 'connection_failed' } as unknown as WSServerEvent);
      return;
    }

    const delay = Math.min(
      1000 * Math.pow(2, this.reconnectAttempts),
      this.maxReconnectDelay,
    );
    this.reconnectAttempts++;

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.doConnect();
    }, delay);
  }

  /** Manually trigger reconnect (e.g. from a "Reconnect" button) */
  reconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.reconnectAttempts = 0;
    this.doConnect();
  }
}

export const wsClient = new WebSocketClient();
